import {
  MODEL_ROUTE_DEFINITIONS,
  isModelRouteId,
  type ModelRouteId,
} from './routes';

export const MODEL_ROUTE_PREFERENCES_V2_KEY = 'ownyou_model_route_preferences_v2';
export const LEGACY_MODEL_PREFERENCES_KEY = 'ownyou_model_preferences';
export const MODEL_ROUTE_PREFERENCES_V2_VERSION = 2;

export const LLM_PROVIDER_IDS = [
  'openai',
  'anthropic',
  'google',
  'groq',
  'deepinfra',
  'xai',
  'ollama',
  'webllm',
] as const;

export type LlmProviderId = typeof LLM_PROVIDER_IDS[number];

export interface ModelRouteSelection {
  provider: LlmProviderId;
  model: string;
  updatedAt: number;
}

export type ModelRouteSelections = Partial<Record<ModelRouteId, ModelRouteSelection>>;
export type ModelIdPreferences = Record<ModelRouteId, string | null>;

export interface ModelRoutePreferencesV2 {
  version: typeof MODEL_ROUTE_PREFERENCES_V2_VERSION;
  routes: Partial<Record<ModelRouteId, ModelRouteSelection>>;
}

export interface ParsedModelRoutePreferencesV2 {
  routeSelections: ModelRouteSelections;
  needsCleanup: boolean;
}

const PROVIDER_ID_SET: ReadonlySet<string> = new Set(LLM_PROVIDER_IDS);

// Canonical IDs are included as identity aliases so every boundary can call
// one normalizer for both stored provider IDs and UI/model-family aliases.
const PROVIDER_ALIASES: Readonly<Record<string, LlmProviderId>> = {
  openai: 'openai',
  anthropic: 'anthropic',
  claude: 'anthropic',
  google: 'google',
  gemini: 'google',
  groq: 'groq',
  deepinfra: 'deepinfra',
  xai: 'xai',
  grok: 'xai',
  ollama: 'ollama',
  webllm: 'webllm',
};

export function normalizeLlmProviderId(provider: unknown): LlmProviderId | null {
  if (typeof provider !== 'string') return null;
  return PROVIDER_ALIASES[provider.trim().toLowerCase()] ?? null;
}

export function isLlmProviderId(provider: unknown): provider is LlmProviderId {
  return typeof provider === 'string' && PROVIDER_ID_SET.has(provider);
}

function toRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function parseSelection(value: unknown): ModelRouteSelection | null {
  const record = toRecord(value);
  if (!record) return null;

  const provider = normalizeLlmProviderId(record.provider);
  const model = typeof record.model === 'string' ? record.model.trim() : '';
  const updatedAt = typeof record.updatedAt === 'number' && Number.isFinite(record.updatedAt)
    ? record.updatedAt
    : null;

  if (!provider || !model || updatedAt === null) return null;

  return { provider, model, updatedAt };
}

export function parseModelRoutePreferencesV2(raw: string | null): ParsedModelRoutePreferencesV2 {
  if (!raw) return { routeSelections: {}, needsCleanup: false };

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { routeSelections: {}, needsCleanup: true };
  }

  const record = toRecord(parsed);
  if (!record || record.version !== MODEL_ROUTE_PREFERENCES_V2_VERSION) {
    return { routeSelections: {}, needsCleanup: Boolean(raw) };
  }

  const routes = toRecord(record.routes);
  if (!routes) return { routeSelections: {}, needsCleanup: true };

  const routeSelections: ModelRouteSelections = {};
  let needsCleanup = false;

  for (const [routeId, value] of Object.entries(routes)) {
    if (!isModelRouteId(routeId)) {
      needsCleanup = true;
      continue;
    }

    const selection = parseSelection(value);
    if (!selection) {
      needsCleanup = true;
      continue;
    }

    const originalProvider = toRecord(value)?.provider;
    if (typeof originalProvider === 'string' && originalProvider !== selection.provider) {
      needsCleanup = true;
    }

    routeSelections[routeId] = selection;
  }

  return { routeSelections, needsCleanup };
}

export function serializeModelRoutePreferencesV2(routeSelections: ModelRouteSelections): string {
  const routes: Partial<Record<ModelRouteId, ModelRouteSelection>> = {};

  for (const route of MODEL_ROUTE_DEFINITIONS) {
    const selection = parseSelection(routeSelections[route.id]);
    if (!selection) continue;
    routes[route.id] = selection;
  }

  return JSON.stringify({
    version: MODEL_ROUTE_PREFERENCES_V2_VERSION,
    routes,
  } satisfies ModelRoutePreferencesV2);
}

export function upsertModelRouteSelection(
  routeSelections: ModelRouteSelections,
  routeId: ModelRouteId,
  selection: (Omit<ModelRouteSelection, 'provider'> & { provider: string }) | null,
): ModelRouteSelections {
  if (!selection) {
    if (!(routeId in routeSelections)) return routeSelections;
    const next: ModelRouteSelections = { ...routeSelections };
    delete next[routeId];
    return next;
  }

  const parsedSelection = parseSelection(selection);
  if (!parsedSelection) {
    return routeSelections;
  }

  const next: ModelRouteSelections = { ...routeSelections };
  next[routeId] = parsedSelection;
  return next;
}

export function createEmptyModelIdPreferences(): ModelIdPreferences {
  return MODEL_ROUTE_DEFINITIONS.reduce((acc, route) => {
    acc[route.id] = null;
    return acc;
  }, {} as ModelIdPreferences);
}

export function deriveModelIdPreferences(
  routeSelections: ModelRouteSelections,
  models: readonly { provider: string; model: string }[],
): ModelIdPreferences {
  const modelIdPreferences = createEmptyModelIdPreferences();
  const available = new Set(
    models
      .map((model) => {
        const provider = normalizeLlmProviderId(model.provider);
        return provider ? `${provider}:${model.model}` : null;
      })
      .filter((key): key is string => Boolean(key)),
  );

  for (const route of MODEL_ROUTE_DEFINITIONS) {
    const selection = routeSelections[route.id];
    if (!selection) continue;
    if (!available.has(`${selection.provider}:${selection.model}`)) continue;
    modelIdPreferences[route.id] = selection.model;
  }

  return modelIdPreferences;
}
